// WHAT A WEBSITE SECTION IS MADE OF — the nine kinds and their fields.
//
// WHY TYPED, AND WHY THIS FILE IS THE ONLY PLACE THEY'RE DEFINED
// The Website panel's output is a HANDOFF DOCUMENT, not a website. Its structure
// IS the deliverable: "Banner → eyebrow / headline / subhead / button" is what
// makes the doc usable by a designer, a done-for-you service, or Claude Code.
// Free-form paragraphs are a draft, not a handoff.
//
// Four things read these definitions, and they must never drift apart:
//   1. the editor      — one labelled input per field (MomentumHub SectionBlock)
//   2. the parser      — the block Brill emits when she drafts one (sectionBlock.js)
//   3. the prompt      — the labels are SENT to Brill in [SECTION_CONTEXT], not
//                        hard-coded in api/_lib/prompts.js, precisely so a field added
//                        here can't leave the prompt behind. api/ is CommonJS and
//                        cannot import this file; passing the spec at call time is
//                        what keeps one source of truth.
//   4. the word count  — a page's word count walks every field of every kind
//
// NINE KINDS: the eight from her mockup plus OTHER. Other is exactly what a
// section was before this — a name and a box of text — and that one addition is
// what makes this a no-migration change:
//   - every existing block reads as `other` with its stored {heading, text}
//     untouched. Legacy `copy` and `hero` values resolve here (see `kindDef`).
//   - `splitCopy` keeps producing Other blocks. Splitting prose at its headings
//     can't know which of eight shapes a block was meant to be, and guessing
//     would file her words under fields she never wrote them for.
//   - `section_type` and `content` jsonb already exist in 0001, so the kinds and
//     their fields need no SQL at all.
//
// WORD GUIDANCE IS GUIDANCE. The number is advice, never a limit enforced on
// her. Nothing here truncates or blocks a save; over budget goes amber and says
// so, and that is the whole enforcement.
//
// ONE NUMBER, AND THE GUIDE NEVER STATES A SECOND ONE. `guide` says what the
// field IS ("the short line above the headline"); `words` says how long. They
// used to both say how long — "2-4 words above the headline" beside a budget of
// 4 — which reads fine until she changes the budget. She set the eyebrow to 6 on
// 2026-09-15 and the row then said "2-4 words" beside "9 / 6 words", and Brill
// was sent "2-4 words above the headline (aim for about 6 words)": two numbers
// in one instruction, neither of them wrong. A guide that restates the default
// is a copy of it, and a copy drifts the moment she edits the original.

/* A field is:
     key    where it lives inside `content`
     label  what the editor calls it
     type   "line" (one-line input) | "prose" (textarea) | "list" (repeatable)
     guide  the length advice shown under the input and sent to Brill
     words  the BUDGET, as a number. The guide is the writing advice ("one or
            two sentences"); this is what the counter counts against and what
            Brill is told to aim for. Never a limit — nothing truncates and
            nothing blocks a save. Omit it where length genuinely does not
            matter (a price, an offer's name, an Other block's prose).
     parts  list fields only. Present → each row is an object of parts; absent →
            each row is a plain string. `paras` is strings; `items` is objects.
     block  what Brill states it under. Defaults to the key uppercased; list
            fields name themselves in the SINGULAR, because the label is repeated
            once per row and `ITEM:` twice reads as two items where `ITEMS:` twice
            reads like a mistake. See `blockLabel`. */

/* THE NUMBERS ARE TRANSCRIBED FROM THE GUIDES, not invented: "up to about 12
   words" is 12, "2-4 words" is 4. Where the guide counts SENTENCES the number is
   a judgement — 30 words for one or two sentences, 60 for two to four — and hers
   to change; every one of them is one edit here, and a template will set them
   per section when templates arrive. */
const F = {
  eyebrow: { key: "eyebrow", label: "Eyebrow", type: "line", guide: "the short line above the headline", words: 4 },
  headline: { key: "head", label: "Headline", type: "line", guide: "the line that has to land", words: 12 },
  heading: { key: "head", label: "Heading", type: "line", guide: "names what follows", words: 6 },
  sub: { key: "sub", label: "Subhead", type: "prose", guide: "one or two sentences", words: 30 },
  btn: { key: "btn", label: "Button text", type: "line", guide: "an action, not a label", words: 5 },
};

/* Ordered as the Add section menu shows them, which is roughly the order a page
   is built in. `other` is last for the same reason it's last in the Add page
   menu: the named options first, then the escape hatch. */
export const SECTION_KINDS = [
  {
    id: "banner",
    label: "Banner",
    hint: "Headline, subhead, button",
    picture: "Your photo, tall, waist up",
    fields: [F.eyebrow, F.headline, F.sub, F.btn],
  },
  {
    id: "about",
    label: "About me",
    hint: "Your story, credibility",
    picture: "Your photo, relaxed and close",
    fields: [
      F.heading,
      { key: "paras", label: "Paragraphs", block: "PARAGRAPH", type: "list", guide: "2-4 sentences each", words: 60, itemLabel: "Paragraph" },
    ],
  },
  {
    id: "services",
    label: "Services",
    hint: "Your offers, priced",
    fields: [
      F.heading,
      {
        key: "items", label: "Services", block: "ITEM", type: "list", itemLabel: "Service",
        parts: [
          { key: "name", label: "Name", type: "line", guide: "the offer's name" },
          { key: "line", label: "One line", type: "prose", guide: "what it does for them", words: 20 },
          { key: "price", label: "Price", type: "line", guide: "e.g. $4,800 or Free" },
        ],
      },
    ],
  },
  {
    id: "freebie",
    label: "Freebie",
    hint: "Lead magnet + email capture",
    picture: "Your freebie's cover",
    fields: [F.heading, F.sub, F.btn],
  },
  {
    id: "testimonials",
    label: "Testimonials",
    hint: "In your clients' words",
    fields: [
      F.heading,
      {
        key: "quotes", label: "Quotes", block: "QUOTE", type: "list", itemLabel: "Quote",
        /* Picked from the Library WORD FOR WORD (her call, 2026-09-17) — a quote
           is the one thing that must not be rewritten. Permission is required:
           this page is read by the public. `row` says what one picked
           testimonial becomes here. */
        library: {
          kind: "testimonial",
          requirePermission: true,
          row: (it) => ({ text: it.text || "", who: it.who || "", photo: it.photo || "" }),
        },
        parts: [
          { key: "text", label: "Quote", type: "prose", guide: "their words, not yours", words: 40 },
          { key: "who", label: "Who said it", type: "line", guide: "name, and what they do", words: 8 },
          /* A link, not an upload: images live in GHL and arrive with that
             connection. The handoff doc needs the photo's address anyway. */
          { key: "photo", label: "Photo link", type: "line", guide: "optional — a link to their headshot" },
        ],
      },
    ],
  },
  {
    id: "faq",
    label: "FAQ",
    hint: "Objections, answered",
    fields: [
      F.heading,
      {
        /* Question and answer are two fields, not one box. Her mockup wrote them
           together, but this doc gets handed to someone who has to lay it out —
           and they can't tell where the question ends if it's one string. */
        key: "qs", label: "Questions", block: "QUESTION", type: "list", itemLabel: "Question",
        parts: [
          { key: "q", label: "Question", type: "line", guide: "as they'd actually ask it", words: 12 },
          { key: "a", label: "Answer", type: "prose", guide: "2-4 sentences, no hedging", words: 60 },
        ],
      },
    ],
  },
  {
    id: "cta",
    label: "Call to action",
    hint: "One button, one next step",
    fields: [F.heading, F.sub, F.btn],
  },
  {
    id: "contact",
    label: "Contact",
    hint: "Form or booking link",
    fields: [F.heading, F.sub],
  },
  {
    /* The kind a section already was. Its keys are `heading` and `text` — the
       exact shape every stored row holds — which is why nothing needs converting. */
    id: "other",
    label: "Other",
    hint: "A name and a block of text",
    picture: "A picture for this section",
    fields: [
      { key: "heading", label: "Block name", type: "line", guide: "what this part of the page is", words: 6 },
      { key: "text", label: "Copy", type: "prose", guide: "as long as it needs to be" },
    ],
  },
];

export const OTHER_KIND = "other";

/* A SECTION'S PICTURE (her ask, 2026-10-23: "add here an ability to add a
   picture … so you can add them quickly from ghl or canva"). Step 3 of
   docs/sites-pictures-spec.md, stored the way §5 decided: in its OWN array,
   `content.pictures[0]`, and never as a field. Not a field on purpose —
   every reader of `fields` (the word count, the labels Brill is sent, the
   parser that writes her drafts back, the "has words" dots) would otherwise
   read a link as copy, and Brill could be asked to write one. A drafted list
   REPLACES the stored one, which is the other reason it lives apart.
   `picture` on a kind is the label of its slot; a kind without one has none. */
export function pictureOf(content) {
  const p = Array.isArray(content?.pictures) ? content.pictures[0] : null;
  return String(p?.url || "").trim();
}
export function withPicture(content, url) {
  const pictures = Array.isArray(content?.pictures) ? [...content.pictures] : [];
  pictures[0] = { ...(pictures[0] || {}), url: String(url || "") };
  return { ...(content || {}), pictures };
}

const BY_ID = new Map(SECTION_KINDS.map((k) => [k.id, k]));

/* Legacy `section_type` values, from before the kinds existed. Both were
   free-form {heading, text} blocks, so both ARE Other — mapping them anywhere
   else would claim a structure their content never had. */
const LEGACY = { copy: OTHER_KIND, hero: OTHER_KIND };

/** The definition for a kind id. Never returns undefined: an unknown value —
 *  a legacy row, a hand-edited one, a kind removed later — falls to Other,
 *  whose two fields can hold anything without losing a word. */
export function kindDef(id) {
  const key = String(id || "").toLowerCase();
  return BY_ID.get(key) || BY_ID.get(LEGACY[key]) || BY_ID.get(OTHER_KIND);
}

/** The kind id a section actually stores, resolved through the legacy map, so
 *  callers compare against a real id rather than repeating the aliases. */
export function kindOf(section) {
  return kindDef(section?.sectionType).id;
}

export function kindLabel(id) {
  return kindDef(id).label;
}

/** The fields a kind holds, in editor order. */
export function fieldsOf(id) {
  return kindDef(id).fields;
}

export function isListField(field) {
  return field?.type === "list";
}

/** What a brand-new section of this kind stores. Lists start empty rather than
 *  with a blank row: an empty list reads as "nothing here yet", where one blank
 *  row reads as a thing she failed to fill in. */
export function emptyContent(id) {
  const content = {};
  for (const f of fieldsOf(id)) content[f.key] = isListField(f) ? [] : "";
  return content;
}

/** One list row's empty value — an object of parts, or "" for a string list. */
export function emptyRow(field) {
  if (!field?.parts) return "";
  const row = {};
  for (const p of field.parts) row[p.key] = "";
  return row;
}

/** A list field's rows, always an array. Stored content is jsonb written by more
 *  than one path (a split, a save, a drafted block), so a bare string where a
 *  list belongs is possible; reading it as one row keeps her words. */
export function rowsOf(content, field) {
  const value = content?.[field.key];
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || value === "") return [];
  return [value];
}

/** Every scrap of text a section holds, flattened — for word counts, and for the
 *  handoff doc when it lands. Empty values are dropped: what's left is what she
 *  has actually written. */
export function sectionValues(section) {
  const content = section?.content || {};
  const out = [];
  for (const f of fieldsOf(section?.sectionType)) {
    if (!isListField(f)) {
      const v = String(content[f.key] ?? "").trim();
      if (v) out.push({ field: f, label: f.label, value: v });
      continue;
    }
    rowsOf(content, f).forEach((row, i) => {
      const n = i + 1;
      const item = f.itemLabel || f.label;
      if (!f.parts) {
        const v = String(row ?? "").trim();
        if (v) out.push({ field: f, index: i, label: `${item} ${n}`, value: v });
        return;
      }
      for (const p of f.parts) {
        const v = String(row?.[p.key] ?? "").trim();
        if (v) out.push({ field: f, part: p, index: i, label: `${item} ${n} — ${p.label}`, value: v });
      }
    });
  }
  return out;
}

/** Has anything at all been written into this section? What the table's dots read
 *  to tell a structured-but-empty page from a written one. */
export function hasContent(section) {
  return sectionValues(section).length > 0;
}

/* Words, counted the way a person counts them: runs of non-space. Markdown marks
   don't count as words — a heading written `## About me` is two words, not three —
   because the number answers "is this page actually written?", and a `##` she
   never typed shouldn't inflate the answer. */
export function wordCount(text) {
  return String(text || "")
    .replace(/[#*_`>|-]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

/** Every word in one section, across all its fields. */
export function sectionWords(section) {
  return sectionValues(section).reduce((n, v) => n + wordCount(v.value), 0);
}

/* ── What Brill is told ───────────────────────────────────────────────────────
   The labels below are SENT to Brill with every drafting message (see
   `sectionSpecLines`), which is what lets the guide in api/_lib/prompts.js stay
   generic. Add a field above and Brill is told about it on the next message. */

/** The label Brill states this field under: its own `block` if it names one,
 *  otherwise its key uppercased. */
export function blockLabel(field) {
  return String(field?.block || field?.key || "").toUpperCase();
}

/**
 * The field list for one kind, as the lines Brill reads and writes back.
 *
 * List fields are stated as ONE label repeated per row, with parts separated by
 * ` | `. A repeated label with a fixed separator is the shape a model gets right
 * most often — indexed labels (`ITEM_1_NAME`) invite it to invent an index 4 and
 * to renumber everything when it rewrites one row.
 */
/**
 * The field's word budget: her override for THIS section if she set one, else
 * the kind's default, else null where length does not matter.
 *
 * ONE ANSWER, asked in one place. The counter on the field, the number she
 * clicks to change, and the number Brill is told to aim at all come through
 * here — if they came from different places, Brill would draft to 12 while the
 * counter measured against 20 and neither would be wrong.
 */
export function fieldWords(field, limits) {
  const set = Number(limits?.[field?.key]);
  if (Number.isFinite(set) && set > 0) return set;
  const n = Number(field?.words);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/* The guide plus its number, for Brill. She sees the same pair on the field —
   the guide beside the label, the count against the number — so what Brill is
   aiming at and what the counter measures are the one fact, said once. */
const specGuide = (f, limits) => {
  const n = fieldWords(f, limits);
  return n ? `${f.guide} (aim for about ${n} words)` : f.guide;
};

/** @param limits her per-field overrides for the section being drafted, if any */
export function sectionSpecLines(id, limits) {
  const lines = [];
  for (const f of fieldsOf(id)) {
    const item = (f.itemLabel || f.label).toLowerCase();
    if (!isListField(f)) {
      lines.push(`${blockLabel(f)}: ${f.label.toLowerCase()} — ${specGuide(f, limits)}`);
    } else if (!f.parts) {
      lines.push(`${blockLabel(f)}: one ${item} — ${specGuide(f, limits)}. Repeat this line for each one.`);
    } else {
      const parts = f.parts.map((p) => `${p.label.toLowerCase()} (${specGuide(p, limits)})`).join(" | ");
      lines.push(`${blockLabel(f)}: ${parts}. Separate the parts with " | ", and repeat this line for each ${item}.`);
    }
  }
  return lines;
}
